import React, { useState, useEffect } from "react";
import { t } from "i18next";
import Icon from "../../../common/components/Icon";
import Loading from "../../../common/components/Loading";
import { getDealerEmployees, addDealerEmployee, deleteDealerEmployee } from "../../../api/dealerEmployees";
import def_user_icon from "../../images/def_user_icon.svg";

const ROLES = ["financier", "manager"];

const roleBadgeClass = {
    financier: "bg-purple-50 text-purple-600",
    manager: "bg-blue-50 text-blue-600",
};

function DealerEmployees({ setActiveTab }) {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [form, setForm] = useState({ name: "", dealer_role: "financier", iin: "", phone: "" });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getDealerEmployees()
            .then(data => setEmployees(data.employees || data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = t('fieldRequired');
        if (!/^\d{12}$/.test(form.iin)) e.iin = t('iinInvalid');
        if (!form.phone.trim()) e.phone = t('fieldRequired');
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleAdd = async () => {
        if (!validate()) return;
        setSubmitting(true);
        try {
            const created = await addDealerEmployee(form);
            setEmployees(prev => [...prev, created]);
            setShowForm(false);
            setForm({ name: "", dealer_role: "financier", iin: "", phone: "" });
            setErrors({});
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteDealerEmployee(id);
            setEmployees(prev => prev.filter(e => e.id !== id));
        } catch (err) {
            console.error(err);
        } finally {
            setDeleteId(null);
        }
    };

    if (loading) return <Loading />;

    return (
        <div className="w-full">
            <Icon src="backicon" onClick={() => setActiveTab(null)} className="block md:hidden fixed left-5 top-[23px] cursor-pointer z-[102]" />

            <div className="bg-white rounded-2xl p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-base font-semibold text-gray-900">{t('employees')}</h2>
                    <button
                        onClick={() => { setShowForm(true); setErrors({}); }}
                        className="flex items-center gap-2 px-4 py-2 bg-[#147F26] hover:bg-[#0f6b1f] text-white text-sm font-medium rounded-xl transition-colors"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        {t('addEmployee')}
                    </button>
                </div>

                {/* Employee list */}
                {employees.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-10">{t('noEmployees')}</p>
                ) : (
                    <ul className="flex flex-col gap-3">
                        {employees.map(emp => (
                            <li key={emp.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                <div className="flex items-center gap-3 min-w-0">
                                    <img
                                        src={emp.profilePicture || def_user_icon}
                                        alt={emp.name}
                                        className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-gray-200"
                                    />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${roleBadgeClass[emp.dealer_role] || "bg-gray-100 text-gray-500"}`}>
                                                {t(emp.dealer_role)}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">{t('iin')}: {emp.iin}</p>
                                        <p className="text-xs text-gray-400">{t('phone')}: {emp.phone}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setDeleteId(emp.id)}
                                    className="ml-3 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                                >
                                    <img src={require("../../../assets/icons/trash.svg").default} alt="delete" className="w-4 h-4 opacity-40 hover:opacity-70" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Add employee modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] px-4">
                    <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
                        <h3 className="text-base font-semibold text-gray-900 mb-5">{t('addEmployee')}</h3>

                        <div className="flex flex-col gap-4">
                            {/* Name */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">{t('fullName')}</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                    placeholder={t('fullName')}
                                    className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors
                                        ${errors.name ? "border-red-400" : "border-gray-200 focus:border-[#147F26]"}`}
                                />
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>

                            {/* Role */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">{t('role')}</label>
                                <select
                                    value={form.dealer_role}
                                    onChange={e => setForm(p => ({ ...p, dealer_role: e.target.value }))}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#147F26] text-sm outline-none transition-colors bg-white"
                                >
                                    {ROLES.map(role => (
                                        <option key={role} value={role}>{t(role)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* IIN */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">{t('iin')}</label>
                                <input
                                    type="text"
                                    value={form.iin}
                                    onChange={e => setForm(p => ({ ...p, iin: e.target.value.replace(/\D/g, "").slice(0, 12) }))}
                                    placeholder="000000000000"
                                    className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors
                                        ${errors.iin ? "border-red-400" : "border-gray-200 focus:border-[#147F26]"}`}
                                />
                                {errors.iin && <p className="text-xs text-red-500 mt-1">{errors.iin}</p>}
                            </div>

                            {/* Phone */}
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">{t('phone')}</label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                                    placeholder="+7 ___ ___ __ __"
                                    className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors
                                        ${errors.phone ? "border-red-400" : "border-gray-200 focus:border-[#147F26]"}`}
                                />
                                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleAdd}
                                disabled={submitting}
                                className="flex-1 py-2.5 rounded-xl bg-[#147F26] hover:bg-[#0f6b1f] text-white text-sm font-medium transition-colors disabled:opacity-60"
                            >
                                {submitting ? t('saving') : t('add')}
                            </button>
                            <button
                                onClick={() => { setShowForm(false); setErrors({}); }}
                                className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
                            >
                                {t('cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirm modal */}
            {deleteId && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] px-4">
                    <div className="bg-white rounded-2xl shadow-xl p-6 w-72">
                        <h3 className="text-base font-semibold text-gray-900 text-center mb-5">
                            {t('deleteEmployeeConfirm')}
                        </h3>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleDelete(deleteId)}
                                className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
                            >
                                {t('yes')}
                            </button>
                            <button
                                onClick={() => setDeleteId(null)}
                                className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
                            >
                                {t('no')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DealerEmployees;
